import React, { useEffect } from 'react';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiFileTextLine, RiArrowLeftLine, RiEdit2Line } from '@remixicon/react';
import { NavigationSidebar } from '@/features/navigation';
import { AircraftSearchBar } from '@/features/search';
import { Shortlist } from '@/features/bids';
import BottomNavBar from '@/components/BottomNavBar';
import { apiClient } from '@/lib/api';
import { getPageTitle } from '@/constants';
import { TrustJetBackground } from '@assets/images';

const AppLayout = ({
  children,
  className = "",
  headerSubtitle,
  headerActions,
}) => {
  const { setBids, pageHeaderOverride } = useAppStore();
  const { userProfile } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isQuickQuote = location.pathname === '/quotes/generate' && searchParams.has('quickquote');
  const isAddClient = location.pathname === '/clients/add';
  const isEditClient = /^\/clients\/[^/]+\/edit$/.test(location.pathname);
  const isTails = location.pathname === '/tails';
  const isClientDetail = /^\/clients\/[^/]+$/.test(location.pathname) && location.pathname !== '/clients/add';
  const showBackButton = isQuickQuote || isTails || isAddClient || isEditClient || isClientDetail || !!pageHeaderOverride;
  const pageTitle = pageHeaderOverride?.title || (isQuickQuote ? "New Quick Quote" : getPageTitle(location.pathname));
  
  // Get current page from location
  const currentRoute = location.pathname;

  const handleNavigate = (link) => {
    navigate(link);
  };
  
  const handleSearch = (query) => {
    // Add your search logic here
    console.log('Search query:', query);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleNotificationClick = () => {
    navigate('/messages');
  };

  /**
   * Use React Query to fetch shortlisted bids
   * - Fetches only once globally
   * - Cached data is reused across all page navigations
   * - Only refetches when manually invalidated
   */
  const { data: bidsData } = useQuery({
    queryKey: ['shortlistedBids', userProfile?.userprofile_id],
    queryFn: async () => {
      const filters = {
        ...(userProfile?.userprofile_id && { broker_id: userProfile.userprofile_id }),
        status: 'DRAFT'
      };

      const data = await apiClient.getBids(0, 10, filters);
      const bidsArray = Array.isArray(data) ? data : (data?.data || []);
      return bidsArray;
    },
    enabled: !!userProfile?.userprofile_id,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Sync bids data to Zustand store when it changes
  useEffect(() => {
    if (bidsData) {
        setBids(bidsData);
    }
  }, [bidsData, setBids]);

  return (
    <div 
      className="flex min-h-screen relative"
      style={{
        backgroundImage: `url(${TrustJetBackground})`,
        backgroundSize: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* White Gradient overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.3) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.8) 65%, rgba(255, 255, 255, 0.95) 80%, rgba(255, 255, 255, 1) 100%)'
        }}
      />
      <div className="group/nav">
        <NavigationSidebar
          currentPage={currentRoute}
          onNavigate={handleNavigate}
          onShowNotifications={handleNotificationClick}
          onShowProfile={handleProfileClick}
        />

        {/* Shortlist Component & Quick Quote - Fixed at bottom left, below sidebar (hidden on mobile) */}
        <div className="fixed bottom-6 left-6 z-40 bg-white/70 rounded-2xl border border-gray-100 p-2 w-16 group-hover/nav:w-56 hover:w-56 transition-all duration-300 ease-in-out group-hover/nav:px-4 hover:px-4 hidden md:flex flex-col gap-2 group/shortlist">
          <Shortlist
            onViewBid={(bid) => console.log('View bid:', bid)}
            useRelativePosition={true}
          />
          {/* Quick Quote Button - Below Shortlist */}
          <button
            onClick={() => navigate('/quotes/generate?quickquote=true')}
            className="flex items-center justify-center gap-2 bg-primary-600 text-white p-3 rounded-full hover:bg-primary-700 transition-colors w-full"
          >
            <RiFileTextLine className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm hidden group-hover/nav:inline group-hover/shortlist:inline whitespace-nowrap">Quick Quote</span>
          </button>
        </div>
      </div>
      
      <main className="flex-1 overflow-auto ml-0 md:ml-24">
        {/* Header Section */}
        <div className="px-4 md:px-6 py-4 pb-[0px] bg-gradient-to-b from-transparent via-transparent to-transparent">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Page Title */}
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-semibold text-white flex items-center gap-3">
                {showBackButton && (
                  <button
                    onClick={() => navigate(-1)}
                    className="p-1 rounded-full hover:bg-white/20 transition-colors"
                    aria-label="Go back"
                  >
                    <RiArrowLeftLine className="w-3 h-3 md:w-5 md:h-5" />
                  </button>
                )}
                {pageTitle}
              </h1>
              {pageHeaderOverride?.subtitle && (
                <p className="text-sm text-white/70 mt-1 ml-9">{pageHeaderOverride.subtitle}</p>
              )}
            </div>

            {/* Header Actions or Search Bar */}
            {pageHeaderOverride?.onEdit ? (
              <button
                onClick={pageHeaderOverride.onEdit}
                className="py-1 px-4 rounded-full border border-white hover:bg-white/20 transition-colors text-white"
              >
                Edit
              </button>
            ) : (
              <div className="w-full md:w-96">
                <AircraftSearchBar
                  onSearch={handleSearch}
                  placeholder="Search Aircraft"
                />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-0 py-2 md:p-6 pb-20 md:pb-6">
          <div className="relative bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
            <div key={location.pathname} className="relative z-10 animate-pageFade">
              {children}
            </div>
            <style>{`@keyframes pageFade { from { opacity: 0; } to { opacity: 1; } } .animate-pageFade { animation: pageFade 0.3s ease-in-out; }`}</style>
          </div>
        </div>
      </main>

      {/* Mobile Shortlist Bar - Above Bottom Navigation */}
      <div className="fixed bottom-20 left-4 right-4 z-40 md:hidden flex gap-2 items-center">
        <Shortlist
          onViewBid={(bid) => console.log('View bid:', bid)}
          useRelativePosition={true}
          isMobile={true}
        />
        {/* Quick Quote Button */}
        <button
          onClick={() => navigate('/quotes/generate?quickquote=true')}
          className="flex-shrink-0 flex items-center justify-center bg-primary-700 text-white w-12 h-12 rounded-full shadow-lg hover:bg-primary-800 transition-colors"
        >
          <RiFileTextLine className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNavBar />
    </div>
  );
};

export default AppLayout;